import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import { getBookingLabel } from "../../utils/industryLabels";
import NewAppointment from "../../components/Appointment/NewAppointment";

const PAGE_SIZE = 10;

const STATUS_COLORS = {
  Success:  { bg: "#dcfce7", text: "#16a34a", dot: "#22c55e" },
  Pending:  { bg: "#fef3c7", text: "#d97706", dot: "#f59e0b" },
  Refunded: { bg: "#dbeafe", text: "#1d4ed8", dot: "#3b82f6" },
  Failed:   { bg: "#fee2e2", text: "#dc2626", dot: "#ef4444" },
};

const TABS = ["All", "Success", "Pending", "Failed", "Refunded"];
const TYPE_TABS = ["All", "Booking", "Subscription"];

// ezp_payment_status id -> status bucket/color, explicit per spec (not a
// string-match heuristic on the name, which could misclassify a renamed
// or new row): 1=Pending, 2/4/7/8/9=Success, 3=Failed, 5/6=Refunded.
const PAYMENT_ID_STATUS = {
  1: "Pending",
  2: "Success",
  3: "Failed",
  4: "Success",
  5: "Refunded",
  6: "Refunded",
  7: "Success",
  8: "Success",
  9: "Success",
};

function normalizeStatus(paymentInt, paymentStatusStr) {
  const byId = PAYMENT_ID_STATUS[paymentInt];
  if (byId) return byId;
  // Fallback for a payment_int the table above doesn't recognize yet
  // (e.g. a newly-added ezp_payment_status row) - best-effort from the name.
  if (!paymentStatusStr) return "Pending";
  const lower = paymentStatusStr.toLowerCase();
  if (lower.includes("refund")) return "Refunded";
  if (lower.includes("fail") || lower.includes("cancel")) return "Failed";
  if (lower.includes("paid") || lower.includes("success") || lower.includes("confirm")) return "Success";
  return "Pending";
}

function formatDateTime(dateStr, timeStr) {
  if (!dateStr) return "—";
  // Handle full datetime strings (e.g. "2024-01-15T14:30:00.000Z" or "2024-01-15 14:30:00")
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  let label;
  if (d.toDateString() === now.toDateString()) label = "Today";
  else if (d.toDateString() === yesterday.toDateString()) label = "Yest";
  else label = d.toLocaleDateString("en", { month: "short", day: "numeric" });
  // If timeStr explicitly provided use it, otherwise extract time from datetime string
  const time = timeStr
    ? timeStr.slice(0, 5)
    : dateStr.includes("T") || dateStr.includes(" ")
      ? d.toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit", hour12: false })
      : null;
  return time ? `${label} · ${time}` : label;
}

export default function Payments() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [page, setPage] = useState(1);
  // One search box: customer name / order / plan, or an exact amount. The server does
  // the searching (after a short pause in typing) so it covers every record.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);
  const searchActive = Boolean(search.trim());
  const [pagination, setPagination] = useState(null);
  const [subscriptionTxns, setSubscriptionTxns] = useState([]);
  const [subsPagination, setSubsPagination] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [stripeVerifying, setStripeVerifying] = useState(false);
  const [showAppointmentPanel, setShowAppointmentPanel] = useState(false);
  const [appointmentPanelMode, setAppointmentPanelMode] = useState("new");
  const [appointmentGroupId, setAppointmentGroupId] = useState(null);

  const closeAppointmentPanel = () => {
    setShowAppointmentPanel(false);
    setAppointmentGroupId(null);
    fetchTransactions(page);
  };

  const userDetails = (() => {
    try {
      const d = localStorage.getItem("userDetails");
      return d && d !== "undefined" ? JSON.parse(d) : null;
    } catch { return null; }
  })();

  const [industry, setIndustry] = useState(null);
  const isClinicService = industry?.industry === 1;
  const isSportsService = industry?.industry === 7;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await apicall.get("ezpMyProfile");
        if (res?.success && res.data?.[0]) {
          setIndustry(res.data[0]);
        }
      } catch (err) {
        console.error("Error fetching vendor profile:", err);
      }
    };
    fetchProfile();
  }, []);

  const currency = userDetails?.currency || "AED";
  const isINR = currency === "INR";
  const sym = isINR ? "₹" : "AED ";

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtCompact = (n) => {
    const num = Number(n || 0);
    if (isINR) {
      if (num >= 100000) return "₹" + (num / 100000).toFixed(1) + "L";
      if (num >= 1000)   return "₹" + (num / 1000).toFixed(1) + "K";
      return "₹" + Math.round(num).toLocaleString("en");
    }
    if (num >= 1000) return "AED " + (num / 1000).toFixed(1) + "K";
    return "AED " + fmt(num);
  };

  const fetchTransactions = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      const res = await apicall.get(
        `get_ezp_booking?page=${pageNum}&limit=${PAGE_SIZE}${debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ""}`
      );
      if (res?.data) {
        setTransactions(res.data);
        setPagination(res.pagination || null);
      }
    } catch (err) {
      console.error("Payments fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  // Subscription purchase/recurring-payment transactions — a separate table
  // (ezp_subscription_payments) from booking transactions, so it's fetched
  // and paginated independently; the "All" tab merges each source's current
  // page rather than a true cross-table pagination (see Payments.jsx plan notes).
  const fetchSubscriptionTransactions = useCallback(async (pageNum = 1) => {
    try {
      setSubsLoading(true);
      const res = await apicall.get(
        `get_ezp_subscription_payments?page=${pageNum}&limit=${PAGE_SIZE}${debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ""}`
      );
      if (res?.data) {
        setSubscriptionTxns(
          res.data.map((t) => ({
            ...t,
            source: "subscription",
            display_booking_id: null,
            group_id: null,
            transaction_id: t.transaction_id || `SUB-${t.id}`,
          }))
        );
        setSubsPagination(res.pagination || null);
      }
    } catch (err) {
      console.error("Subscription payments fetch error:", err);
    } finally {
      setSubsLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (typeFilter !== "Subscription") fetchTransactions(page);
  }, [page, typeFilter, fetchTransactions]);

  useEffect(() => {
    if (typeFilter !== "Booking") fetchSubscriptionTransactions(page);
  }, [page, typeFilter, fetchSubscriptionTransactions]);

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const handleTypeFilterChange = (type) => {
    setTypeFilter(type);
    setPage(1);
  };

  // ── Handle Stripe post-redirect (session_id + gid in URL) ─────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeSession = params.get("stripe_session");
    const gid = params.get("gid");
    if (!stripeSession || !gid) return;

    // Clean URL immediately so refresh doesn't re-trigger
    window.history.replaceState({}, "", window.location.pathname);

    const verifyAndComplete = async () => {
      setStripeVerifying(true);
      try {
        const res = await apicall.post(
          { session_id: stripeSession, group_id: gid },
          "complete_stripe_payment"
        );
        if (res?.success) {
          const confirmedNoun = isClinicService ? "Appointment" : "Booking";

          toast.success(`Payment Successful! ${confirmedNoun} confirmed.`);
          // Clear stale pending gid so next new booking starts fresh
          sessionStorage.removeItem("utilstop_checkout_pending_gid");
          sessionStorage.removeItem("utilstop_checkout_state");
          fetchTransactions(1);
        } else {
          toast.error("Payment verification failed: " + (res?.message || "Unknown error"));
        }
      } catch (e) {
        toast.error("Could not verify Stripe payment. Contact support.");
      } finally {
        setStripeVerifying(false);
      }
    };

    verifyAndComplete();
  }, []);

  // When filter tab changes, reset to page 1
  const handleFilterChange = (tab) => {
    setActiveFilter(tab);
    setPage(1);
  };

  const txnStatus = (t) => normalizeStatus(t.payment_int, t.payment_status);
  const txnAmount = (t) => Number(t.total_paid_amount) > 0
    ? t.total_paid_amount
    : Number(t.total) > 0
      ? t.total
      : t.price;

  // Rows for the current type tab. "All" merges each source's current page
  // (client-side) rather than a true cross-table paginated query — a known
  // simplification; each source's own tab paginates that source correctly.
  const bookingRows = transactions.map((t) => ({ ...t, source: "booking" }));
  const sourceRows =
    typeFilter === "Booking" ? bookingRows
    : typeFilter === "Subscription" ? subscriptionTxns
    : [...bookingRows, ...subscriptionTxns].sort(
        (a, b) => new Date(b.payment_created_at || 0) - new Date(a.payment_created_at || 0)
      );

  // Stat cards computed from current page data (all-time totals need server-side aggregation; this is page-level)
  const paid = sourceRows.filter((t) => txnStatus(t) === "Success");
  const totalRevenue = paid.reduce((s, t) => s + Number(txnAmount(t) || 0), 0);
  const avgTxn = paid.length ? totalRevenue / paid.length : 0;
  const pendingList = sourceRows.filter((t) => txnStatus(t) === "Pending");
  const pendingSettlement = pendingList.reduce((s, t) => s + Number(txnAmount(t) || 0), 0);
  const refundsList = sourceRows.filter((t) => txnStatus(t) === "Refunded");
  const refundsTotal = refundsList.reduce((s, t) => s + Number(txnAmount(t) || 0), 0);

  const totalCount =
    typeFilter === "Booking" ? (pagination?.total_count ?? bookingRows.length)
    : typeFilter === "Subscription" ? (subsPagination?.total_count ?? subscriptionTxns.length)
    : (pagination?.total_count ?? bookingRows.length) + (subsPagination?.total_count ?? subscriptionTxns.length);

  const statCards = [
    { label: "Total Revenue",       value: fmtCompact(totalRevenue),      change: `${paid.length} paid`,         icon: isINR ? "₹" : "$", color: "#3b82f6", bg: "#eff6ff" },
    { label: "Transactions",        value: String(totalCount), change: isClinicService ? "total appointments" : "total bookings", icon: "≡",             color: "#8b5cf6", bg: "#f5f3ff" },
    { label: "Avg. Transaction",    value: fmtCompact(avgTxn),            change: isClinicService ? "per paid appointment" : "per paid booking",            icon: "↗",             color: "#10b981", bg: "#ecfdf5" },
    { label: "Pending Settlement",  value: fmtCompact(pendingSettlement), change: `${pendingList.length} pending`, icon: "⏳",           color: "#f59e0b", bg: "#fffbeb" },
    { label: "Refunds",             value: fmtCompact(refundsTotal),      change: `${refundsList.length} refund`, icon: "↩",            color: "#ef4444", bg: "#fef2f2" },
  ];

  const filtered = sourceRows.filter((t) => activeFilter === "All" || txnStatus(t) === activeFilter);

  const totalPages =
    typeFilter === "Booking" ? (pagination?.total_pages ?? 1)
    : typeFilter === "Subscription" ? (subsPagination?.total_pages ?? 1)
    : Math.max(pagination?.total_pages ?? 1, subsPagination?.total_pages ?? 1);

  const isLoadingRows = typeFilter === "Subscription" ? subsLoading : loading;

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px 28px", fontFamily: "inherit" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "#0f172a" }}>Payments</h2>
          <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 13 }}>All transactions across gateways</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {/* <button style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", border: "1.5px solid #e2e8f0", borderRadius: 9, background: "#fff", fontSize: 13, fontWeight: 600, color: "#334155", cursor: "pointer" }}>
            🔗 Payment Link
          </button> */}
          <button
            onClick={() => {
              if (isClinicService) {
                setAppointmentPanelMode("new");
                setAppointmentGroupId(null);
                setShowAppointmentPanel(true);
              } else {
                navigate("/vendor_bookings");
              }
            }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", border: "none", borderRadius: 9, background: "#d01c4c", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" }}
          >
             {`New ${getBookingLabel(industry)}`}
          </button>
        </div>
      </div>

      {/* Stripe verification banner */}
      {stripeVerifying && (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "12px 18px", marginBottom: 18, fontSize: 13, color: "#1d4ed8", display: "flex", alignItems: "center", gap: 10 }}>
          <span>⏳</span> Verifying Stripe payment, please wait…
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 14, marginBottom: 28, flexWrap: "wrap" }}>
        {statCards.map((card, i) => (
          <div key={i} style={{ flex: "1 1 150px", background: "#fff", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 5px rgba(0,0,0,0.06)", minWidth: 140 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: card.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: card.color, fontWeight: 700, marginBottom: 14 }}>
              {card.icon}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 3, lineHeight: 1.2 }}>{card.value}</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6, fontWeight: 500 }}>{card.label}</div>
            <div style={{ fontSize: 11, color: card.color, fontWeight: 600 }}>↑ {card.change}</div>
          </div>
        ))}
      </div>

      {/* Transactions table */}
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {/* Table top bar */}
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
            Recent Transactions
            <span style={{ fontWeight: 500, fontSize: 13, color: "#94a3b8", marginLeft: 8 }}>
              ({totalCount} total)
            </span>
          </span>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {TYPE_TABS.map((type) => (
                <button
                  key={type}
                  onClick={() => handleTypeFilterChange(type)}
                  style={{
                    padding: "5px 14px",
                    border: `1.5px solid ${typeFilter === type ? "#0f172a" : "#e2e8f0"}`,
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: typeFilter === type ? 700 : 500,
                    background: typeFilter === type ? "#0f172a" : "#fff",
                    color: typeFilter === type ? "#fff" : "#64748b",
                    cursor: "pointer",
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleFilterChange(tab)}
                  style={{
                    padding: "5px 14px",
                    border: `1.5px solid ${activeFilter === tab ? "#4f46e5" : "#e2e8f0"}`,
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: activeFilter === tab ? 700 : 500,
                    background: activeFilter === tab ? "#eef2ff" : "#fff",
                    color: activeFilter === tab ? "#4f46e5" : "#64748b",
                    cursor: "pointer",
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search + amount filter */}
        <div style={{ padding: "12px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, order or amount"
            style={{ flex: "1 1 240px", minWidth: 200, padding: "7px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none" }}
          />
          {searchActive && (
            <span
              onClick={() => setSearch("")}
              style={{ fontSize: 12, color: "#d01c4c", fontWeight: 600, cursor: "pointer" }}
            >
              Clear
            </span>
          )}
        </div>

        {/* Column headers */}
        <div style={{ display: "grid", gridTemplateColumns: "0.8fr 2.2fr 1.1fr 1.2fr 0.9fr 1fr 1fr 36px", padding: "10px 24px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
{[
  "BOOKING / PLAN",
  "TXN ID",
  "DATE / TIME",
  isClinicService ? "PATIENT" : isSportsService ? "PLAYER" : "CUSTOMER",
  "AMOUNT",
  "GATEWAY",
  "STATUS",
  "",
].map((col, i) => (
            <div key={i} style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col}</div>
          ))}
        </div>

        {/* Rows */}
        {isLoadingRows ? (
          <div style={{ padding: "56px 24px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
            Loading transactions…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "56px 24px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
            No transactions found
          </div>
        ) : (
          filtered.map((txn, i) => {
            const isSubscription = txn.source === "subscription";
            const status = txnStatus(txn);
            const sc = STATUS_COLORS[status] || STATUS_COLORS.Pending;
            // Show the booking's actual payment status name (ezp_payment_status.name,
            // e.g. "Cash paid"/"Paid Online"/"Pending") instead of a hardcoded id
            // mapping - that previously showed "Paid Online" for id 7 ("Cash paid").
            const statusLabel = txn.payment_status || status;
            const txnId = txn.transaction_id;
            const gatewayLabel = txn.payment_gateway
              ? txn.payment_gateway.charAt(0).toUpperCase() + txn.payment_gateway.slice(1)
              : "";
            const amount = txnAmount(txn);

            return (
              <div
                key={`${txn.source || "booking"}-${txn.id || i}`}
                onClick={() => {
                  if (isSubscription) {
                    navigate(txn.plan_id ? `/subscription/${txn.plan_id}` : "/subscription");
                    return;
                  }
                  const dest = txn.display_booking_id || txn.group_id;
                  if (isClinicService) {
                    setAppointmentPanelMode("edit");
                    setAppointmentGroupId(dest || null);
                    setShowAppointmentPanel(true);
                  } else {
                    navigate(dest ? `/vendor_bookings/${dest}` : "/vendor_bookings");
                  }
                }}
                style={{ display: "grid", gridTemplateColumns: "0.8fr 2.2fr 1.1fr 1.2fr 0.9fr 1fr 1fr 36px", padding: "13px 24px", borderBottom: i < filtered.length - 1 ? "1px solid #f8fafc" : "none", cursor: "pointer", background: "#fff", alignItems: "center" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <div style={{ fontSize: 13, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {isSubscription ? (txn.order_ref || txn.plan_name || "Subscription") : (txn.display_booking_id || txn.group_id || "—")}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={txnId}>{txnId}</div>
                <div style={{ fontSize: 13, color: "#475569" }}>
                  {txn.payment_created_at
                    ? formatDateTime(txn.payment_created_at)
                    : "-"}
                </div>
                <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {txn.user_name?.trim() || "Walk-in"}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                  {sym}{fmt(amount)}
                </div>
                <div style={{ fontSize: 13, color: "#64748b" }}>{gatewayLabel || "—"}</div>
                <div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: sc.bg, fontSize: 12, fontWeight: 600, color: sc.text }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc.dot, display: "inline-block", flexShrink: 0 }} />
                    {statusLabel}
                  </span>
                </div>
                <div style={{ color: "#cbd5e1", fontSize: 18, textAlign: "right", lineHeight: 1 }}>›</div>
              </div>
            );
          })
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Page {page} of {totalPages}
              {pagination && ` · ${pagination.total_count} total records`}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  padding: "6px 16px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 7,
                  fontSize: 13,
                  fontWeight: 600,
                  background: page <= 1 ? "#f8fafc" : "#fff",
                  color: page <= 1 ? "#cbd5e1" : "#334155",
                  cursor: page <= 1 ? "default" : "pointer",
                }}
              >
                ← Prev
              </button>
              {/* Page number pills */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{
                      padding: "6px 12px",
                      border: `1.5px solid ${p === page ? "#4f46e5" : "#e2e8f0"}`,
                      borderRadius: 7,
                      fontSize: 13,
                      fontWeight: p === page ? 700 : 500,
                      background: p === page ? "#eef2ff" : "#fff",
                      color: p === page ? "#4f46e5" : "#334155",
                      cursor: "pointer",
                      minWidth: 36,
                    }}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{
                  padding: "6px 16px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 7,
                  fontSize: 13,
                  fontWeight: 600,
                  background: page >= totalPages ? "#f8fafc" : "#fff",
                  color: page >= totalPages ? "#cbd5e1" : "#334155",
                  cursor: page >= totalPages ? "default" : "pointer",
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <Offcanvas
        show={showAppointmentPanel}
        onHide={closeAppointmentPanel}
        placement="end"
        backdrop={true}
        style={{ width: "500px", minWidth: "500px", maxWidth: "500px", flexShrink: 0 }}
      >
        <Offcanvas.Header closeButton className="custom-border-bottom p-4">
          <Offcanvas.Title className="page-heading">
            {appointmentPanelMode === "edit" ? "" : "New Appointment"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 d-flex flex-column">
          <div className="appointment-card">
            <div className="appointment-body">
              {showAppointmentPanel && (
                <NewAppointment
                  onClose={closeAppointmentPanel}
                  mode={appointmentPanelMode}
                  groupId={appointmentPanelMode === "edit" ? appointmentGroupId : undefined}
                />
              )}
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
